import { queryKeys } from '@/lib/query-keys';
import type {
  LineBillingDetail,
  LineBillingPeriod,
} from '@octave/contracts';
import {
  useQueries,
  useQuery,
  } from '@tanstack/react-query';
import {
  billingClient,
  } from '@/lib/api/ts-rest';


export const useLineBillingDetail = (
  lineId: string | undefined,
  month?: number,
  year?: number
) => {
  return useQuery<LineBillingDetail, Error>({
    queryKey: queryKeys.billing.lineDetail(lineId, month, year),
    queryFn: async () => {
      const res = await billingClient.getLineBillingDetail({
        params: { lineId: lineId! },
        query: { month, year },
      });
      if (res.status !== 200) throw new Error('Failed to load billing detail');
      return res.body;
    },
    enabled: !!lineId,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });
};

export const useLineBillingPeriods = (lineId: string | undefined) => {
  return useQuery<LineBillingPeriod[], Error>({
    queryKey: queryKeys.billing.linePeriods(lineId),
    queryFn: async () => {
      const res = await billingClient.getLineBillingPeriods({
        params: { lineId: lineId! },
      });
      if (res.status !== 200) throw new Error('Failed to load billing periods');
      return res.body;
    },
    enabled: !!lineId,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });
};

export interface LineBillingSeriesPoint {
  month: number;
  year: number;
  subscriptionTotal: number;
  consumptionTotal: number;
}

/**
 * Per-line billing across all its billed months (oldest → newest), for the trend
 * chart. Reuses the `line-billing-detail` query keys so each month shares its cache
 * with the single-month tab below.
 */

export const useLineBillingSeries = (lineId: string | undefined) => {
  const { data: periods } = useLineBillingPeriods(lineId);
  const ordered = periods
    ? [...periods].sort((a, b) => a.year - b.year || a.month - b.month)
    : [];

  const results = useQueries({
    queries: ordered.map((p) => ({
      queryKey: queryKeys.billing.lineDetail(lineId, p.month, p.year),
      queryFn: async () => {
        const res = await billingClient.getLineBillingDetail({
          params: { lineId: lineId! },
          query: { month: p.month, year: p.year },
        });
        if (res.status !== 200)
          throw new Error('Failed to load billing detail');
        return res.body;
      },
      enabled: !!lineId,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    })),
  });

  const series: LineBillingSeriesPoint[] = results
    .map((r) => r.data)
    .filter((d): d is LineBillingDetail => !!d && d.hasData)
    .map((d) => ({
      month: d.month,
      year: d.year,
      subscriptionTotal: d.subscriptionTotal,
      consumptionTotal: d.consumptionTotal,
    }));

  return { series, isLoading: results.some((r) => r.isLoading) };
};
